import Modal from "components/Modals/Modal";
import CloudMultiplex from "components/OrgPages/ConnectCloud/CloudMultiplex";
import React, { useContext, useState } from "react";
import Button from "components/UI-lib/Button";
import { OrgContext } from "contexts/OrgContext";
import { CloudCredContext } from "contexts/CloudCredContext";
import { isIsolatedCloudCred } from "utils/cloudCreds";
import SimpleTransition from "components/SimpleTransition";
import { CardElement, useElements, useStripe } from "@stripe/react-stripe-js";
import InlineNotification from "contexts/Notifications/InlineNotifications";
import { UserContext } from "contexts/UserContext";
import Dropdown, { DropdownItem } from "components/UI-lib/Dropdown";
import { allPublicCloudsName } from "components/Environment/Create/utils";
import { DarkModeContext } from "contexts/DarkModeContext";
import {
  STRIPE_CONNECTION_ERROR,
  useStripeClientSecret,
} from "server/brevdeploy/hooks/useStripeClientSecret";
import { BREV_SUPPORT_EMAIL } from "constants/index";

export interface ConnectCloudsOrCardModalProps {
  show: boolean;
  setShow: (show: boolean) => void;
  onCancel?: () => void;
  onSuccess?: () => void;
  setSelectedCloudName: (cloud: string) => void;
  selectedCloudName: string;
  setNoCardOnFile: (isFree: boolean) => void;
  noCardOnFile?: boolean;
}

const ConnectCloudsOrCardModal: React.FC<ConnectCloudsOrCardModalProps> = ({
  show,
  setShow,
  onCancel = () => {},
  onSuccess = () => {},
  setSelectedCloudName,
  selectedCloudName,
  setNoCardOnFile,
}) => {
  const [cardLoading, setCardLoading] = useState(false);
  const stripe = useStripe();
  const elements = useElements();
  const orgContext = useContext(OrgContext);
  const { cloudCreds } = useContext(CloudCredContext);
  const userContext = useContext(UserContext);
  const [inlineAlertSeverity, setInlineAlertSeverity] = useState<
    "error" | "warning" | "info" | "success"
  >("info");
  const [inlineAlertMessage, setInlineAlertMessage] = useState("");
  const { darkMode } = useContext(DarkModeContext);
  const { clientSecret, clientSecretLoading, clientSecretError } =
    useStripeClientSecret({
      activeOrgId: orgContext.activeOrgId,
      enabled: show,
    });

  const handleSubmitCreditCard = async () => {
    setCardLoading(true);
    // Stripe.js has not yet loaded.
    // Make sure to disable form submission until Stripe.js has loaded.
    if (!stripe || !elements) {
      setCardLoading(false);
      return;
    }
    if (!clientSecret) {
      setInlineAlertSeverity("error");
      setInlineAlertMessage(STRIPE_CONNECTION_ERROR);
      setCardLoading(false);
      return;
    }

    const cardElement = elements.getElement(CardElement);
    if (!cardElement) {
      setCardLoading(false);
      return;
    }

    const res = await stripe.confirmCardSetup(clientSecret, {
      payment_method: {
        card: cardElement,
        metadata: {
          userId: userContext.me?.id || "",
          orgId: orgContext.activeOrgId,
        },
      },
    });

    if (res.error) {
      // Show error to your customer (for example, payment details incomplete)
      console.error(res.error.message);
      setInlineAlertSeverity("error");
      setInlineAlertMessage(
        `Unable to submit credit card details. Please try again or contact support at ${BREV_SUPPORT_EMAIL}.`,
      );
    } else {
      setNoCardOnFile(false);
      cardElement.clear();
      onSuccess();
      // await getPaymentMethods();
      //   if (onComplete) await onComplete();
    }
    setCardLoading(false);
  };

  return (
    <Modal
      isOpen={show}
      setOpen={setShow}
      onClose={() => {
        setShow(false);
      }}
      // onSuccess={async () => {
      //   await onSuccess();
      //   setShow(false);
      // }}
      title={
        !!cloudCreds.find(isIsolatedCloudCred)
          ? "Change Clouds or Connect credit card"
          : "Connect a cloud or credit card"
      }
      body={
        <div className="w-[450px] pb-10">
          {!!cloudCreds.find(isIsolatedCloudCred) ? (
            <>
              <p className="text-sm text-gray-500 mb-1">
                Select a different cloud instead of Brev Cloud ™️
              </p>
              <div className="w-1/2">
                <Dropdown
                  label="Cloud"
                  value={selectedCloudName}
                  onChange={(cloud) => setSelectedCloudName(cloud)}
                >
                  {[
                    ...cloudCreds
                      .filter(isIsolatedCloudCred)
                      .map((cloudCred) => cloudCred.name),
                    allPublicCloudsName,
                  ].map((ws) => (
                    <DropdownItem displayValue={ws} value={ws} />
                  ))}
                </Dropdown>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                * Brev.dev is free if you use your own cloud
              </p>
            </>
          ) : (
            <>
              <p className="text-sm text-gray-500 mb-2">
                Brev.dev is free if you connect your own cloud
              </p>
              <CloudMultiplex />
              <p className="text-xs text-gray-500 mt-2">
                *Already connected? Make sure it's selected in your hardware
              </p>
            </>
          )}
          {/* <hr className="mt-2"></hr> */}
          <div className="text-sm text-gray-500 mt-2 mb-2 ml-2 flex flex-row items-center justify-start">
            <hr className="w-[11px] mr-1"></hr>
            or
            <hr className="w-[11px] ml-1"></hr>
          </div>
          <p className="text-sm text-gray-500 mb-2">
            Connect a credit card and use Brev Cloud ™️
          </p>
          <InlineNotification
            className="mb-2"
            show={!!clientSecretError || !!inlineAlertMessage}
            severity={
              clientSecretError ? "error" : inlineAlertSeverity || "info"
            }
            text={clientSecretError || inlineAlertMessage}
            autoClose={false}
            hideCloseButton={!!clientSecretError}
            onClose={() => setInlineAlertMessage("")}
          />
          <SimpleTransition show={true}>
            <div className="flex flex-row w-full">
              <div className="ph-mask w-[90%]">
                <CardElement
                  options={{
                    style: {
                      base: {
                        color: darkMode ? "#cffafe" : "black",
                      },
                    },
                    classes: {
                      base: "rounded-md border-gray-300 dark:border-zinc-800 shadow-sm p-3 sm:text-sm stripe-border",
                      focus: "border-highlight ring-cyan-500",
                    },
                  }}
                />
              </div>
              {/* <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse col-span-4"> */}
              <Button
                className="ml-3"
                label="Save"
                type="primary"
                disabled={cardLoading || clientSecretLoading || !clientSecret}
                loading={cardLoading}
                onClick={() => {
                  handleSubmitCreditCard();
                }}
              />
              {/* </div> */}
            </div>
          </SimpleTransition>
        </div>
      }
      //   disableSuccess={name === "" || port === ""}
      confirmLabel="Create"
    />
  );
};

export default ConnectCloudsOrCardModal;
